// Low-level typed HTTP client. All api/ modules go through this so auth headers,
// base URL, and error handling live in exactly one place.

import { getApiBase } from '../lib/apiBase';

const API_BASE = getApiBase();

export class ApiError extends Error {
  status: number;
  // Full parsed error body when present, so callers can read structured detail
  // (e.g. publish-validation `violations`) beyond the message.
  data?: Record<string, unknown>;

  constructor(message: string, status: number, data?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

function buildHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let cleanPath = path;
  if (cleanPath.startsWith('/api/')) cleanPath = cleanPath.slice(4);
  else if (cleanPath.startsWith('api/')) cleanPath = cleanPath.slice(3);
  if (!cleanPath.startsWith('/')) cleanPath = `/${cleanPath}`;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${cleanPath}`, {
      ...init,
      headers: { ...buildHeaders(), ...(init?.headers || {}) },
      credentials: 'include',
    });
  } catch {
    throw new ApiError('Network error — please check your connection or that the server is running.', 0);
  }

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let body: Record<string, unknown> | undefined;
    try {
      body = await res.json();
      if (body && typeof body.message === 'string') message = body.message;
    } catch {
      /* response had no JSON body */
    }
    throw new ApiError(message, res.status, body);
  }

  return (await res.json()) as T;
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path);
}

// Fetches a path as raw text (e.g. CSV export) with the auth header attached.
export async function apiGetText(path: string): Promise<string> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: buildHeaders(),
    credentials: 'include',
  });
  if (!res.ok) throw new ApiError(`Request failed (${res.status})`, res.status);
  return res.text();
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
}

export function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined });
}

export function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined });
}

export function apiDelete<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}

/**
 * Multipart upload. Deliberately does not go through request(): the browser must
 * set Content-Type itself so the multipart boundary is included, and the shared
 * buildHeaders() always pins application/json.
 */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      body: form,
      headers,
      credentials: 'include',
    });
  } catch {
    throw new ApiError('Network error — could not reach the server to upload.', 0);
  }
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    let body: Record<string, unknown> | undefined;
    try {
      body = await res.json();
      if (body && typeof body.message === 'string') message = body.message;
    } catch { /* no JSON body */ }
    throw new ApiError(message, res.status, body);
  }
  return res.json() as Promise<T>;
}
